// src/components/SummariesDashboard.js
// Author: Sunil Jain & Gemini AI
// Description: A flexible dashboard for displaying lists of check-in summaries.
// REFACTORED: Now accepts title and icon as props for reusability.

import React from 'react';
import {
    Box, Typography, Paper, List, ListItem, ListItemText, ListItemIcon, Button
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article'; // Default icon
import { format, parseISO } from 'date-fns';

export default function SummariesDashboard({ summaries, onOpenModal, title, icon: Icon = ArticleIcon }) {
    if (!summaries || summaries.length === 0) {
        // To maintain layout consistency, render a placeholder if no data is available.
        return (
            <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Icon sx={{ mr: 1, color: 'text.secondary' }} /> {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 4, fontStyle: 'italic' }}>
                    No data logged for this period.
                </Typography>
            </Paper>
        );
    }

    const sortedSummaries = [...summaries].sort((a, b) => {
        const dateA = a.submitted_at ? new Date(a.submitted_at) : 0;
        const dateB = b.submitted_at ? new Date(b.submitted_at) : 0;
        return dateB - dateA;
    });

    return (
        <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Icon sx={{ mr: 1, color: 'text.secondary' }} /> {title}
            </Typography>
            <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                {sortedSummaries.map(summary => {
                    const submittedDate = summary.submitted_at ? format(parseISO(summary.submitted_at), 'MMM d, yyyy') : 'N/A';
                    const primaryText = summary.item_name || 'General Check-in';

                    return (
                        <ListItem
                            key={summary.id}
                            divider
                            sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 2, '&:last-child': { borderBottom: 0 } }}
                        >
                            <ListItemIcon sx={{minWidth: 'auto', mr: 1.5}}>
                                <Icon color="info" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body1" fontWeight="500">{primaryText}</Typography>}
                                secondary={`Submitted: ${submittedDate}`}
                                sx={{ m: 0, flexGrow: 1 }}
                            />
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => onOpenModal(summary, summary)}
                                sx={{ minWidth: '80px' }}
                            >
                                Details
                            </Button>
                        </ListItem>
                    );
                })}
            </List>
        </Paper>
    );
}